import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/DropdownMenu";
import { Button } from "@/components/ui/Button";
import { ChevronDownIcon } from "lucide-react";
import { IntervalPreset } from "../types";

interface IntervalPresetConfig {
  value: IntervalPreset;
  labelKey: string;
  fallbackLabel: string;
  days?: number;
}

const INTERVAL_PRESETS: IntervalPresetConfig[] = [
  {
    value: "7d",
    labelKey: "chart.intervals.7days",
    fallbackLabel: "7 days",
    days: 7,
  },
  {
    value: "30d",
    labelKey: "chart.intervals.30days",
    fallbackLabel: "30 days",
    days: 30,
  },
  {
    value: "3m",
    labelKey: "chart.intervals.3months",
    fallbackLabel: "3 months",
    days: 90,
  },
  {
    value: "6m",
    labelKey: "chart.intervals.6months",
    fallbackLabel: "6 months",
    days: 180,
  },
  {
    value: "12m",
    labelKey: "chart.intervals.12months",
    fallbackLabel: "12 months",
    days: 365,
  },
  {
    value: "currentCycle",
    labelKey: "chart.intervals.currentCycle",
    fallbackLabel: "Current Cycle",
  },
  {
    value: "custom",
    labelKey: "chart.intervals.custom",
    fallbackLabel: "Custom",
  },
] as const;

export interface IntervalSelectorProps {
  selectedPreset: IntervalPreset;
  onPresetChange: (preset: IntervalPreset) => void;
  className?: string;
}

export const IntervalSelector: FC<IntervalSelectorProps> = ({
  selectedPreset,
  onPresetChange,
  className,
}) => {
  const { t } = useTranslation();

  const getPresetButtonText = useCallback(() => {
    const preset = INTERVAL_PRESETS.find((p) => p.value === selectedPreset);
    if (!preset) return selectedPreset;

    const translatedLabel = t(preset.labelKey, preset.fallbackLabel);

    // Show "Last {interval}" for time-based presets
    if (preset.value !== "currentCycle" && preset.value !== "custom") {
      return t("chart.intervals.lastPeriod", "Last {{period}}", {
        period: translatedLabel,
      });
    }

    // Show as-is for "Current Cycle" and "Custom"
    return translatedLabel;
  }, [selectedPreset, t]);

  return (
    <DropdownMenu data-testid="interval-selector">
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`gap-2 ${className || ""}`}
          aria-label={t(
            "chart.intervals.selectInterval",
            "Select time interval",
          )}
          data-testid="interval-button"
        >
          {getPresetButtonText()}
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid="interval-dropdown">
        {INTERVAL_PRESETS.map((preset) => (
          <DropdownMenuItem
            key={preset.value}
            onClick={() => onPresetChange(preset.value)}
            className="cursor-pointer"
            data-testid={`interval-option-${preset.value}`}
          >
            {t(preset.labelKey, preset.fallbackLabel)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
